import { diaDaSemana, diasDoPeriodo } from '../../escala';
import type { CelulaDaEscala, MudancaDeCelula, PessoaDaEscala, TurnoDaEscala } from '../../escala';

/**
 * CAIXAS (dono, 26/10/2026): "aba Caixas com revezamento — folga, férias, caixa, horário, almoço,
 * tudo". Regras puras (sem tela) para dar teste:
 *
 *   quem é caixa      função "Caixa" na ficha, ou quem está num "Caixa N" na semana;
 *   o dia de cada um  trabalho (posto, entrada–saída, almoço) ou o motivo de não estar (folga,
 *                     folga de feriado, feriado, férias, falta, atestado, sem escala);
 *   revezamento       o posto gira a cada dia (quem foi Caixa 1 hoje vai para o 2 amanhã) e o
 *                     almoço é escalonado: dois caixas nunca almoçam ao mesmo tempo — e quem
 *                     almoça primeiro também gira;
 *   alertas           almoço junto, dois no mesmo caixa, caixa sem ninguém.
 *
 * O posto mora no `setor` da célula ("Caixa 1"); o almoço do dia, em almocoInicio/almocoFim
 * (vazio = o da ficha da pessoa, senão o do turno).
 */

const CAIXA_RE = /^caixa\s*(\d{1,2})$/i;

/** "Caixa 2" → 2; qualquer outro setor → null. */
export function numeroDoCaixa(setor: string | null | undefined): number | null {
  const m = setor ? CAIXA_RE.exec(setor.trim()) : null;
  return m?.[1] ? Number(m[1]) : null;
}
export const nomeDoPosto = (n: number) => `Caixa ${String(n)}`;

export function ehCaixa(p: PessoaDaEscala, celulas: readonly CelulaDaEscala[]): boolean {
  if (p.funcao && /caixa/i.test(p.funcao)) return true;
  return celulas.some((c) => c.pessoaId === p.id && numeroDoCaixa(c.setor) !== null);
}

export type SituacaoDoCaixa =
  'TRABALHO' | 'FOLGA' | 'COMPENSADA' | 'FERIADO' | 'FERIAS' | 'FALTA' | 'ATESTADO' | 'VAZIO';

export const ROTULO_DA_SITUACAO_DO_CAIXA: Readonly<Record<SituacaoDoCaixa, string>> = {
  TRABALHO: 'Trabalha',
  FOLGA: 'Folga',
  COMPENSADA: 'Folga de feriado',
  FERIADO: 'Feriado',
  FERIAS: 'Férias',
  FALTA: 'Falta',
  ATESTADO: 'Atestado',
  VAZIO: 'Sem escala',
};

export interface DiaDoCaixa {
  readonly pessoa: PessoaDaEscala;
  readonly dia: string;
  readonly celula: CelulaDaEscala | null;
  readonly turno: TurnoDaEscala | null;
  readonly situacao: SituacaoDoCaixa;
  /** Só no dia de trabalho. */
  readonly posto: number | null;
  readonly entrada: string | null;
  readonly saida: string | null;
  readonly almocoInicio: string | null;
  readonly almocoFim: string | null;
  /** O almoço veio do dia (revezamento) ou da ficha/turno? */
  readonly almocoDoDia: boolean;
}

export function diaDoCaixa(
  pessoa: PessoaDaEscala,
  dia: string,
  celula: CelulaDaEscala | null,
  turnos: ReadonlyMap<string, TurnoDaEscala>,
): DiaDoCaixa {
  const turno = celula ? (turnos.get(celula.turno) ?? null) : null;
  const situacao: SituacaoDoCaixa = !celula ? 'VAZIO' : (turno?.categoria ?? 'VAZIO');
  if (situacao !== 'TRABALHO')
    return {
      pessoa,
      dia,
      celula,
      turno,
      situacao,
      posto: null,
      entrada: null,
      saida: null,
      almocoInicio: null,
      almocoFim: null,
      almocoDoDia: false,
    };
  const h = pessoa.horario;
  const fichaTemHora = !!(h.entrada || h.saida);
  const doDia = !!(celula?.almocoInicio && celula.almocoFim);
  const fichaTemAlmoco = !!(h.almocoInicio && h.almocoFim);
  return {
    pessoa,
    dia,
    celula,
    turno,
    situacao,
    posto: numeroDoCaixa(celula?.setor),
    entrada: (fichaTemHora ? h.entrada : turno?.entrada) ?? null,
    saida: (fichaTemHora ? h.saida : turno?.saida) ?? null,
    almocoInicio: doDia
      ? (celula?.almocoInicio ?? null)
      : fichaTemAlmoco
        ? h.almocoInicio
        : (turno?.almocoInicio ?? null),
    almocoFim: doDia
      ? (celula?.almocoFim ?? null)
      : fichaTemAlmoco
        ? h.almocoFim
        : (turno?.almocoFim ?? null),
    almocoDoDia: doDia,
  };
}

/* ---------- horas ---------- */
export const minutos = (h: string) => Number(h.slice(0, 2)) * 60 + Number(h.slice(3, 5));
export const hora = (m: number) =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const cruza = (a1: number, a2: number, b1: number, b2: number) => a1 < b2 && b1 < a2;

/* ---------- alertas ---------- */
export interface AlertaDoCaixa {
  readonly dia: string;
  readonly texto: string;
}

/** Almoço junto, dois no mesmo caixa — dia a dia. */
export function alertasDoDia(dia: string, trabalham: readonly DiaDoCaixa[]): AlertaDoCaixa[] {
  const out: AlertaDoCaixa[] = [];
  // dois no mesmo caixa só é problema no mesmo turno (manhã × noite revezam o mesmo caixa)
  const porPosto = new Map<string, string[]>();
  for (const t of trabalham)
    if (t.posto !== null) {
      const k = `${grupoDoTurno(t)}|${String(t.posto)}`;
      porPosto.set(k, [...(porPosto.get(k) ?? []), t.pessoa.nome]);
    }
  for (const [k, nomes] of porPosto)
    if (nomes.length > 1)
      out.push({
        dia,
        texto: `${nomes.join(' e ')} estão no ${nomeDoPosto(Number(k.split('|')[1]))} no mesmo turno.`,
      });
  const comAlmoco = trabalham.filter((t) => t.almocoInicio && t.almocoFim);
  for (let i = 0; i < comAlmoco.length; i += 1)
    for (let j = i + 1; j < comAlmoco.length; j += 1) {
      const a = comAlmoco[i];
      const b = comAlmoco[j];
      if (!a?.almocoInicio || !a.almocoFim || !b?.almocoInicio || !b.almocoFim) continue;
      if (
        cruza(
          minutos(a.almocoInicio),
          minutos(a.almocoFim),
          minutos(b.almocoInicio),
          minutos(b.almocoFim),
        )
      )
        out.push({
          dia,
          texto: `${a.pessoa.nome} e ${b.pessoa.nome} almoçam juntos (${a.almocoInicio}–${a.almocoFim} × ${b.almocoInicio}–${b.almocoFim}).`,
        });
    }
  return out;
}

/* ---------- revezamento ---------- */
const ALMOCO_PADRAO = 60;

/**
 * Monta o revezamento da semana para quem TRABALHA: posto girando por dia e almoço escalonado.
 * Devolve só o que muda (setor e almoço do dia); turno, folga, férias etc. não são tocados.
 *
 *   posto   por turno (manhã / tarde-noite), a ordem do quadro girada pelo número do dia
 *           (seg = 0): idx + dia → Caixa 1..N;
 *   almoço  a base de cada um (ficha/turno; sem nada: 4 h depois da entrada, 1 h), na ordem
 *           girada pelo dia; se cruzar com o de outro caixa que está trabalhando na hora, empurra
 *           para depois do outro. Não passa da saída (aí fica a base e o alerta avisa).
 */
export function montarRevezamento(
  semana: string,
  caixas: readonly PessoaDaEscala[],
  celulas: ReadonlyMap<string, CelulaDaEscala>,
  turnos: ReadonlyMap<string, TurnoDaEscala>,
): MudancaDeCelula[] {
  const out: MudancaDeCelula[] = [];
  const dias = diasDoPeriodo(semana, addDias(semana, 6));
  for (const [i, dia] of dias.entries()) {
    const doDia = caixas
      .map((p) => diaDoCaixa(p, dia, celulas.get(`${p.id}|${dia}`) ?? null, turnos))
      .filter((d) => d.situacao === 'TRABALHO');
    // cada turno tem os seus caixas: a manhã é Caixa 1, 2… e a noite também
    for (const grupo of ['MANHA', 'TARDE'] as const) {
      const trabalham = doDia.filter((d) => grupoDoTurno(d) === grupo);
      out.push(...revezarGrupo(dia, i, trabalham, turnos));
    }
  }
  return out;
}

/** Manhã = entra antes do meio-dia; tarde/noite = o resto. Os postos se repetem em cada turno. */
export function grupoDoTurno(d: DiaDoCaixa): 'MANHA' | 'TARDE' {
  return d.entrada && d.entrada >= '12:00' ? 'TARDE' : 'MANHA';
}

function revezarGrupo(
  dia: string,
  i: number,
  trabalham: readonly DiaDoCaixa[],
  turnos: ReadonlyMap<string, TurnoDaEscala>,
): MudancaDeCelula[] {
  const out: MudancaDeCelula[] = [];
  const n = trabalham.length;
  if (n === 0) return out;
  const giro = (k: number) => (((k + i) % n) + n) % n;
  // almoço: a base é a da ficha/turno — o almoço do dia de antes não conta (o revezamento refaz)
  const base = trabalham.map((d) => {
    const b = baseDoAlmoco(semAlmocoDoDia(d, turnos));
    return { d, ini: b.ini, dur: b.dur };
  });
  // a ordem de quem escolhe primeiro gira a cada dia: hoje o A fica com a hora dele e o B vai
  // para depois; amanhã o contrário
  const ordem = [...base].sort((a, b) => a.ini - b.ini || a.d.pessoa.ordem - b.d.pessoa.ordem);
  const girada = ordem.map((_, k) => ordem[giro(k)]).filter((x) => x !== undefined);
  const marcados: { ini: number; fim: number; d: DiaDoCaixa }[] = [];
  for (const x of girada) {
    let ini = x.ini;
    // empurra enquanto cruzar com o almoço de outro caixa que está no turno nessa hora
    for (let guarda = 0; guarda < 20; guarda += 1) {
      const choque = marcados.find(
        (m) => cruza(ini, ini + x.dur, m.ini, m.fim) && trabalhaNaHora(m.d, ini, ini + x.dur),
      );
      if (!choque) break;
      ini = choque.fim;
    }
    // não passa da saída: fica a base (o alerta avisa do almoço junto)
    const saida = x.d.saida ? minutos(x.d.saida) : 24 * 60;
    if (ini + x.dur > saida) ini = x.ini;
    marcados.push({ ini, fim: ini + x.dur, d: x.d });
  }
  for (const [k, d] of trabalham.entries()) {
    const m = marcados.find((x) => x.d === d);
    const posto = giro(k) + 1;
    const almocoInicio = m ? hora(m.ini) : null;
    const almocoFim = m ? hora(m.fim) : null;
    const c = d.celula;
    if (
      c &&
      numeroDoCaixa(c.setor) === posto &&
      (c.almocoInicio ?? null) === almocoInicio &&
      (c.almocoFim ?? null) === almocoFim
    )
      continue;
    out.push({
      pessoaId: d.pessoa.id,
      dia,
      turno: c?.turno ?? null,
      setor: nomeDoPosto(posto),
      almocoInicio,
      almocoFim,
    });
  }
  return out;
}

function semAlmocoDoDia(d: DiaDoCaixa, turnos: ReadonlyMap<string, TurnoDaEscala>): DiaDoCaixa {
  if (!d.almocoDoDia || !d.celula) return d;
  return diaDoCaixa(d.pessoa, d.dia, { ...d.celula, almocoInicio: null, almocoFim: null }, turnos);
}

function baseDoAlmoco(d: DiaDoCaixa): { ini: number; dur: number } {
  if (d.almocoInicio && d.almocoFim)
    return { ini: minutos(d.almocoInicio), dur: minutos(d.almocoFim) - minutos(d.almocoInicio) };
  const entrada = d.entrada ? minutos(d.entrada) : 8 * 60;
  return { ini: entrada + 4 * 60, dur: ALMOCO_PADRAO };
}

/** O outro caixa está trabalhando (não almoçando nem fora do turno) nesse intervalo? */
function trabalhaNaHora(d: DiaDoCaixa, ini: number, fim: number): boolean {
  const e = d.entrada ? minutos(d.entrada) : 0;
  const s = d.saida ? minutos(d.saida) : 24 * 60;
  return cruza(ini, fim, e, s);
}

function addDias(dia: string, n: number): string {
  return new Date(Date.parse(`${dia}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
}

/** Domingos trabalhados na semana (para o resumo). */
export const ehDomingo = (dia: string) => diaDaSemana(dia) === 0;
